import React, { useState } from 'react';
import MuiFormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect, { SelectProps as MuiSelectProps } from '@mui/material/Select';
import { Typography } from '../typography';
import { Box } from '../box';
import {
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  SxProps,
  Theme,
} from '@mui/material';

/**
 * Props for the `Select` component.
 *
 * Extends MUI's `Select` props and adds custom features.
 */
type SelectComponentProps = {
  /**
   * An array of options to be displayed in the dropdown.
   */
  options: any[];

  /**
   * A mapper for customizing option display. It allows mapping:
   * - `id` for the option's value
   * - `name` for the option's displayed text
   * - `icon` for an optional icon component
   *
   * @example
   * ```tsx
   * const displayMapper = { id: 'value', name: 'label', icon: 'icon' };
   * ```
   */
  displayMapper?: Record<string, string>;

  /**
   * The system prop that allows defining system overrides as well as additional CSS styles for Select's FormControl Wrapper.
   */
  formSx?: SxProps<Theme> | undefined;

  /**
   * boolean for showing error message.
   */
  error?: boolean;

  /**
   * helper text that will show under the select component.
   */
  helperText?: React.ReactNode;

  /**
   * show is loading
   */
  isLoading?: boolean;

  /**
   * optional content to show in the select text field when loading
   */
  loadingOption?: { title?: string; icon?: React.ElementType<any> };

  /**
   * Overrides the onChange of MuiSelectProps and expose value and raw selected option.
   */
  onChange: (value: any, raw: any) => void;
} & Omit<MuiSelectProps, 'onChange'>;

/**
 * `Select` is a custom dropdown component built on top of MUI's `Select`.
 * It supports:
 * - Custom option rendering using `displayMapper`
 * - Optional icons alongside option names
 *
 * @param {SelectComponentProps} props - Props for the `Select` component.
 * @returns {JSX.Element} A dropdown select component with customizable options.
 *
 * @example
 * ```tsx
 * import { Select } from './Select';
 * import { ReactComponent as UserIcon } from './icons/user.svg';
 *
 * const options = [
 *   { value: '1', label: 'Option 1', icon: UserIcon },
 *   { value: '2', label: 'Option 2' },
 * ];
 *
 * const MyComponent = () => (
 *   <Select
 *     label="My Select"
 *     options={options}
 *     displayMapper={{ id: 'value', name: 'label', icon: 'icon' }}
 *   />
 * );
 * ```
 *
 * @category Component
 * @document SelectComponentUsage.md
 */
export const Select = ({
  options,
  displayMapper,
  formSx,
  error,
  helperText,
  isLoading,
  loadingOption,
  onChange,
  ...props
}: SelectComponentProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const forceToggleMenu = () => {
    setOpen(!open);
  };

  const LoadingIconComponent = loadingOption?.icon;
  return (
    <MuiFormControl fullWidth variant={props.variant} sx={formSx} error={error}>
      {/* Optional label */}
      {props.label && <InputLabel id="select-label">{props.label}</InputLabel>}

      {/* The Select dropdown */}
      <MuiSelect
        labelId="select-label"
        id="select"
        {...props}
        open={open} // Controlled open state
        onClose={() => setOpen(false)} // Close on menu close
        onOpen={() => setOpen(true)}
        input={
          isLoading ? (
            <OutlinedInput
              label={props.label}
              placeholder={'loadingOption?.title'}
              sx={{ zIndex: 1 }}
              onClick={() => forceToggleMenu()}
              startAdornment={
                <InputAdornment
                  position="start"
                  sx={{
                    position: 'absolute',
                    left: 14,
                  }}
                >
                  {LoadingIconComponent && <LoadingIconComponent />}
                  <Typography variant="bodyLG" sx={{ marginLeft: '12px' }}>
                    {loadingOption?.title}
                  </Typography>
                </InputAdornment>
              }
              endAdornment={
                isLoading ? (
                  <InputAdornment
                    position="start"
                    sx={{
                      position: 'absolute',
                      right: 25,
                    }}
                  >
                    <CircularProgress size={15} />
                  </InputAdornment>
                ) : undefined
              }
            />
          ) : undefined
        }
        value={isLoading ? '' : props.value}
      >
        {isLoading ? (
          <MenuItem disabled>Loading...</MenuItem>
        ) : (
          options.map((option: string, index) => {
            let id: string | number = option;
            let name: string | number = option;
            let IconComponent = undefined;

            // Map option properties if displayMapper is provided
            if (displayMapper) {
              const displaySource = option as unknown as Record<
                string,
                string | number
              >;

              id = displaySource[displayMapper.id] || 'id';
              name = displaySource[displayMapper.name] || 'name';

              if (displayMapper.icon) {
                IconComponent = (option as unknown as Record<string, any>)[
                  displayMapper.icon
                ] as React.ElementType;
              }
            }

            return (
              <MenuItem
                key={index}
                value={id}
                onClick={() => onChange(id, option)}
              >
                <Box display="flex" alignItems="center" gap="12px">
                  {IconComponent && <IconComponent />}
                  <Typography fontSize={16}>{name}</Typography>
                </Box>
              </MenuItem>
            );
          })
        )}
      </MuiSelect>
      {error && helperText && (
        <Typography
          fontSize={12}
          sx={{
            color: theme => theme.palette.error.main,
            margin: '3px 14px 0',
          }}
        >
          {helperText}
        </Typography>
      )}
    </MuiFormControl>
  );
};
